import React, { useRef, useState } from 'react';
import { Camera, Trash2 } from 'lucide-react';
import { accountAPI } from '../../../../api/client';
import { apiError } from '../../../../lib/host';
import { useT } from '../../../../lib/i18n';
import { usePreferences } from '../../preferences/PreferencesContext';
import { Avatar } from '../../shell/ProfileMenu';

const SIZE = 256;

/** Square-crops and shrinks a photo to 256 px JPEG in the browser (a few dozen KB to store). */
function shrink(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const side = Math.min(img.width, img.height);
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = SIZE;
      canvas.getContext('2d').drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, SIZE, SIZE);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.86));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('bad image')); };
    img.src = url;
  });
}

/** The profile photo with a camera button to change it (and remove it). */
export default function AvatarPicker({ name, onError, onSaved }) {
  const { t } = useT();
  const { avatar, setAvatar } = usePreferences();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const choose = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\/(png|jpeg|webp|gif)$/.test(file.type)) { onError(t('Choose a PNG, JPEG or WebP picture')); return; }
    if (file.size > 8 * 1024 * 1024) { onError(t('Choose a picture smaller than 8 MB')); return; }
    setBusy(true);
    try {
      const data = await shrink(file);
      const res = await accountAPI.saveAvatar(data);
      setAvatar(res.data.avatar);
      onSaved(t('Profile photo updated.'));
    } catch (err) {
      onError(apiError(err, t('That picture could not be used. Try another one.')));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await accountAPI.removeAvatar();
      setAvatar(null);
      onSaved(t('Profile photo removed.'));
    } catch (err) {
      onError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="st-avatar">
      <Avatar src={avatar} name={name} size={124} />
      <button type="button" className="st-avatar-camera" title={t('Change photo')} aria-label={t('Change photo')} disabled={busy} onClick={() => input.current?.click()}>
        <Camera size={15} />
      </button>
      {avatar && <button type="button" className="st-avatar-remove" disabled={busy} onClick={remove}><Trash2 size={12} />{t('Remove')}</button>}
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={choose} />
    </div>
  );
}
